import axiosInstance from '../utils/axiosConfig';
import {
  LoyaltyProgram,
  LoyaltyProgramFormData,
  LoyaltyProgramsResponse,
  LoyaltyProgramResponse,
  CalculatePointsRequest,
  CalculatePointsResponse
} from '../types/loyaltyProgram';

const API_BASE_URL = '/loyalty-programs';

export const loyaltyProgramService = {
  // Get all loyalty programs
  getLoyaltyPrograms: async (params?: {
    search?: string;
    isActive?: boolean;
    page?: number;
    limit?: number;
  }): Promise<LoyaltyProgramsResponse> => {
    const response = await axiosInstance.get<LoyaltyProgramsResponse>(API_BASE_URL, { params });
    return response.data;
  },

  // Get single loyalty program
  getLoyaltyProgram: async (id: string): Promise<LoyaltyProgramResponse> => {
    const response = await axiosInstance.get<LoyaltyProgramResponse>(`${API_BASE_URL}/${id}`);
    return response.data;
  },

  // Get active loyalty program
  getActiveLoyaltyProgram: async (): Promise<LoyaltyProgramResponse> => {
    const response = await axiosInstance.get<LoyaltyProgramResponse>(`${API_BASE_URL}/active`);
    return response.data;
  },

  // Create new loyalty program
  createLoyaltyProgram: async (data: LoyaltyProgramFormData): Promise<LoyaltyProgramResponse> => {
    const response = await axiosInstance.post<LoyaltyProgramResponse>(API_BASE_URL, data);
    return response.data;
  },

  // Update loyalty program
  updateLoyaltyProgram: async (
    id: string,
    data: Partial<LoyaltyProgramFormData>
  ): Promise<LoyaltyProgramResponse> => {
    const response = await axiosInstance.put<LoyaltyProgramResponse>(`${API_BASE_URL}/${id}`, data);
    return response.data;
  },

  // Delete loyalty program
  deleteLoyaltyProgram: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.delete<{ success: boolean; message: string }>(
      `${API_BASE_URL}/${id}`
    );
    return response.data;
  },

  // Toggle program status
  toggleLoyaltyProgramStatus: async (id: string): Promise<LoyaltyProgramResponse> => {
    const response = await axiosInstance.patch<LoyaltyProgramResponse>(
      `${API_BASE_URL}/${id}/toggle-status`
    );
    return response.data;
  },

  // Calculate points for amount
  calculatePoints: async (
    id: string,
    data: CalculatePointsRequest
  ): Promise<CalculatePointsResponse> => {
    const response = await axiosInstance.post<CalculatePointsResponse>(
      `${API_BASE_URL}/${id}/calculate-points`,
      data
    );
    return response.data;
  },

  // Get customer loyalty points
  getCustomerLoyaltyPoints: async (
    customerId: string,
    params?: {
      type?: string;
      search?: string;
      dateFrom?: string;
      dateTo?: string;
      page?: number;
      limit?: number;
    }
  ) => {
    const response = await axiosInstance.get(`/customers/${customerId}/loyalty-points`, { params });
    return response.data;
  },

  // Get customer balance
  getCustomerBalance: async (customerId: string) => {
    const response = await axiosInstance.get(`/customers/${customerId}/loyalty-balance`);
    return response.data;
  },

  // Get customer statistics
  getCustomerStatistics: async (customerId: string) => {
    const response = await axiosInstance.get(`/customers/${customerId}/loyalty-statistics`);
    return response.data;
  },

  // Get active program from customer endpoint
  getActiveProgramForCustomer: async () => {
    const response = await axiosInstance.get('/customers/active-loyalty-program');
    return response.data;
  },

  // Process loyalty points manually
  processLoyaltyPoints: async (customerId?: string) => {
    const response = await axiosInstance.post('/customers/process-loyalty-points', {
      customerId
    });
    return response.data;
  },

  // Process expired loyalty points
  processExpiredPoints: async () => {
    const response = await axiosInstance.post('/customers/process-expired-points');
    return response.data;
  },

  // Delete all loyalty points
  deleteAllLoyaltyPoints: async () => {
    const response = await axiosInstance.delete('/customers/delete-all-points');
    return response.data;
  },

  // Update schedule settings
  updateScheduleSettings: async (
    id: string,
    data: {
      enabled: boolean;
      frequency: 'daily' | 'weekly';
      time: string;
      day?: number;
    }
  ) => {
    const response = await axiosInstance.put(`${API_BASE_URL}/${id}/schedule`, data);
    return response.data;
  },

  // Get schedule settings
  getScheduleSettings: async (id: string) => {
    const response = await axiosInstance.get(`${API_BASE_URL}/${id}/schedule`);
    return response.data;
  },

  // Adjust customer loyalty points manually
  adjustCustomerPoints: async (data: {
    contactId: string;
    points: number;
    type: 'add' | 'deduct';
    expiryDate?: string;
    notes?: string;
  }) => {
    const response = await axiosInstance.post(`${API_BASE_URL}/adjust-points`, data);
    return response.data;
  }
};

export default loyaltyProgramService;